// PA-3b — full-screen "view any restaurant" picker for TOC platform staff.
// Purely presentational: the list comes from GET /api/platform/restaurants (staff-only);
// selecting one sets x-view-restaurant-id on subsequent (read-only, audited) API calls.
import React from 'react'
import type { PlatformRestaurant } from '../../api/platform'

export function StaffRestaurantPicker({
  restaurants,
  loading,
  error,
  onPick,
  onOpenConsole,
}: {
  restaurants: PlatformRestaurant[]
  loading: boolean
  error: string | null
  onPick: (r: PlatformRestaurant) => void
  // m0-5c-1 — present only for a staff member who holds the platform.admin grant. Opens the
  // cross-tenant admin console (a separate surface from the read-only tenant view).
  onOpenConsole?: () => void
}) {
  return (
    <div style={styles.center}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Platform staff</h1>
        <p style={styles.sub}>
          {onOpenConsole
            ? 'Choose a restaurant — as TOC Admin you get full access, and every change is recorded.'
            : 'Choose a restaurant to view (read-only).'}
        </p>

        {onOpenConsole && (
          <button type="button" style={styles.console} onClick={onOpenConsole}>
            Open platform-admin console →
          </button>
        )}

        {loading && <p style={styles.msg}>Loading restaurants…</p>}
        {error && <p style={{ ...styles.msg, color: '#dc2626' }}>{error}</p>}
        {!loading && !error && restaurants.length === 0 && (
          <p style={styles.msg}>No restaurants found.</p>
        )}

        <ul style={styles.list}>
          {restaurants.map((r) => (
            <li key={r.id}>
              <button type="button" style={styles.item} onClick={() => onPick(r)}>
                {r.name}
                {r.lifecycleStatus && r.lifecycleStatus !== 'ACTIVE' && r.lifecycleStatus !== 'TRIAL' && (
                  <span style={styles.status}> · {r.lifecycleStatus.charAt(0) + r.lifecycleStatus.slice(1).toLowerCase()}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: 'system-ui, sans-serif',
  },
  card: {
    width: 'min(440px, 90vw)',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '2rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  h1: { fontSize: 20, margin: '0 0 4px', color: '#0f172a' },
  sub: { fontSize: 14, color: '#64748b', margin: '0 0 1rem' },
  msg: { fontSize: 14, color: '#64748b', margin: '0.5rem 0' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  item: {
    width: '100%',
    textAlign: 'left',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: 15,
    color: '#0f172a',
  },
  console: {
    width: '100%',
    textAlign: 'left',
    background: '#0f172a',
    border: '1px solid #0f172a',
    borderRadius: 8,
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    marginBottom: '1rem',
  },
  status: { color: '#b91c1c', fontSize: 13, fontWeight: 600 },
}
